import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';

/**
 * The wire shape, not the server's Drizzle-inferred `Invoice` type from
 * 'types': that type declares `createdAt: Date`, which is only true
 * server-side. Over JSON it arrives as an ISO string. The server names its
 * columns rather than selecting the row, so Polar's own customer and
 * subscription ids never reach the browser (see invoices.ts).
 */
export type InvoiceRow = {
  id: number;
  amount: number | null;
  refundedAmount: number | null;
  currency: string | null;
  paid: boolean;
  billingReason: string | null;
  createdAt: string;
};

type UseInvoicesParams = { enabled?: boolean };

/** The caller's own invoices. A client org under an agency has none (the agency is charged), so the list is empty. */
export const useInvoices = ({ enabled = true }: UseInvoicesParams = {}) => {
  const apiClient = useClient();
  const { user } = useAuth();

  const {
    data: invoices,
    isLoading,
    isError,
    refetch,
  } = useQuery<InvoiceRow[]>({
    queryKey: ['BILLING_INVOICES'],
    enabled: !!user && enabled,
    queryFn: () => apiClient.get('/billing/invoices'),
  });

  return { invoices, isLoading, isError, refetch };
};
